import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addFarmerIdentificationDetails as addFarmerIdentificationDetailsAction,
  updateFarmerIdentificationDetails as updateFarmerIdentificationDetailsAction,
} from "@/actions";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";
import { getTitleName } from "@/utils/wfpManagerConfigurationUtils";

import { FARMER_IDENTIFICATION_FORM_ID, type FarmerIdentificationFormValues, Form } from "../Form";
import { toValueFormat } from "../../constants";
import {
  type EntityId,
  type FarmerIdentificationModalOptions,
  type FarmerIdentificationType,
  type SetFarmerIdentificationModalOptions,
} from "../../types";

type FarmerIdentificationListResponse =
  | FarmerIdentificationType[]
  | {
      results?: FarmerIdentificationType[];
    };

interface FarmerIdentificationListState {
  data: FarmerIdentificationListResponse;
  isFetching: boolean;
}

interface RootState {
  listFarmerIdentification: FarmerIdentificationListState;
}

interface StateProps {
  farmerIdentification: FarmerIdentificationType[];
  isFetching: boolean;
}

interface OwnProps {
  modalOptions?: FarmerIdentificationModalOptions;
  setModalOptions: SetFarmerIdentificationModalOptions;
}

interface DispatchProps {
  addFarmerIdentificationDetails: (values: FarmerIdentificationFormValues) => void;
  updateFarmerIdentificationDetails: (id: EntityId, values: FarmerIdentificationFormValues) => void;
}

type FarmerIdentificationEditModalProps = StateProps & OwnProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: FarmerIdentificationModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const isEntityId = (id: FarmerIdentificationModalOptions["id"]): id is EntityId =>
  id !== undefined && id !== "add";

// The detail endpoint answers with a value_format the select cannot match, so the form is
// prefilled from the list row instead.
export const formatInitialValues = (
  identificationType?: FarmerIdentificationType
): Partial<FarmerIdentificationFormValues> | undefined =>
  identificationType && {
    active: Boolean(identificationType.active),
    code: identificationType.code ?? "",
    name: identificationType.name ?? "",
    value_format: toValueFormat(identificationType.value_format),
    value_maximum_length: identificationType.value_maximum_length ?? undefined,
    value_minimum_length: identificationType.value_minimum_length ?? undefined,
  };

const getDialogTitle = (
  modalOptions: FarmerIdentificationModalOptions,
  t: (key: string) => string
) => {
  const actionTitleKey = getTitleName(modalOptions.type);
  const baseTitle = t("configuration.item.farmerIdentification");

  return actionTitleKey ? `${baseTitle} ${t(actionTitleKey)}` : baseTitle;
};

export const FarmerIdentificationEditModal = ({
  addFarmerIdentificationDetails,
  farmerIdentification = [],
  isFetching,
  modalOptions = CLOSED_MODAL_OPTIONS,
  setModalOptions,
  updateFarmerIdentificationDetails,
}: FarmerIdentificationEditModalProps) => {
  const { t } = useTranslation();
  const { id, type } = modalOptions;
  const isViewMode = type === "view";
  const initialValues = useMemo(() => {
    if (type === "create" || !isEntityId(id)) {
      return undefined;
    }

    return formatInitialValues(
      farmerIdentification.find(identificationType => String(identificationType.id) === String(id))
    );
  }, [farmerIdentification, id, type]);

  const handleClose = () => {
    setModalOptions(CLOSED_MODAL_OPTIONS);
  };

  const handleSubmit = (formValues: FarmerIdentificationFormValues) => {
    if (type === "edit" && isEntityId(id)) {
      updateFarmerIdentificationDetails(id, formValues);
    }

    if (type === "create") {
      addFarmerIdentificationDetails(formValues);
    }

    handleClose();
  };

  return (
    <Dialog
      open={Boolean(modalOptions.isOpen)}
      onOpenChange={open => (open ? undefined : handleClose())}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{getDialogTitle(modalOptions, t)}</DialogTitle>
        </DialogHeader>
        <Form
          initialValues={initialValues}
          isFetching={isFetching}
          modalOptions={modalOptions}
          onSubmit={handleSubmit}
        />
        <DialogFooter>
          {isViewMode ? null : (
            <WFPButton type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </WFPButton>
          )}
          {isViewMode ? (
            <WFPButton type="button" onClick={handleClose}>
              {t("common.back")}
            </WFPButton>
          ) : (
            <WFPButton type="submit" form={FARMER_IDENTIFICATION_FORM_ID}>
              {t("common.save")}
            </WFPButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const getListResults = (data: FarmerIdentificationListResponse): FarmerIdentificationType[] =>
  Array.isArray(data) ? data : (data.results ?? []);

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    farmerIdentification: getListResults(state.listFarmerIdentification.data),
    isFetching: state.listFarmerIdentification.isFetching,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    addFarmerIdentificationDetails: values =>
      dispatchAction(
        dispatch,
        addFarmerIdentificationDetailsAction(values, "", true, {
          page_size: ALL_ITEMS_PAGE_SIZE,
        })
      ),
    updateFarmerIdentificationDetails: (id, values) =>
      dispatchAction(
        dispatch,
        updateFarmerIdentificationDetailsAction(id, values, "", true, {
          page_size: ALL_ITEMS_PAGE_SIZE,
        })
      ),
  };
};

export const ConnectedFarmerIdentificationEditModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(FarmerIdentificationEditModal);
